import Button from "@/component/Button";
import SelectionList from "@/component/SelectionList";
import { speakerNameLimitPattern } from "@/constants/record";
import SpeakerAvatar from "@/pages/Voice/SpearkerAvatar";
import collaborationStore from "@/store/collaboration.store";
import Popover from "@/component/PopoverRc";
import { ReactNode, useMemo, useState } from "react";
import MoreIcon from "@/assets/summary/more.svg?react";
import fridayReporter from "@/utils/friday";
import InputWithClear from "@/component/InputWithClear";
import PinyinMatch from 'pinyin-match';

// 检查是否匹配跳字模式（如"灰狼"匹配"灰太狼"）
const checkSkipMatch = (text: string, search: string): boolean => {
  let textIndex = 0;
  let searchIndex = 0;

  while (textIndex < text.length && searchIndex < search.length) {
    if (text[textIndex] === search[searchIndex]) {
      searchIndex++;
    }
    textIndex++;
  }

  return searchIndex === search.length;
};

export interface ISpeakerPopoverProps {
  onEditSingle: (name: string) => void;
  onEditAll?: (name: string, oldSpeakerName: string) => void;
  originValue: string;
}

const CollaborationSpeakerPopover: React.FC<ISpeakerPopoverProps> = ({ onEditAll, onEditSingle, originValue }) => {
  // 使用 collaborationStore 的 recentSpeakerList
  const speakerList = collaborationStore((state) => state.recentSpeakerList);
  const getAvatarIndex = collaborationStore((state) => state.getAvatarIndex);

  const [inputValue, setInputValue] = useState<string>('');

  const editDisabled = useMemo(() => !inputValue || inputValue === originValue, [inputValue, originValue]);

  const list = useMemo(() => {
    const targetValue = inputValue || originValue;
    const searchValue = inputValue.toLowerCase();

    // 1. 如果没有输入搜索词，显示完整列表
    if (!searchValue) {
      // 如果当前值在列表中，将其置顶
      const element = speakerList.find((item) => item.speakerName[0] === targetValue);
      if (element) {
        return [element, ...speakerList.filter((item) => item.speakerName[0] !== targetValue)];
      }
      return speakerList;
    }

    // 2. 有搜索词时，进行模糊搜索
    const filteredList = speakerList.filter((item) => {
      const name = item.speakerName[0];
      const nameLower = name.toLowerCase();
      
      // 2.1 支持从名字开头搜索
      const matchStart = nameLower.startsWith(searchValue);
      // 2.2 支持从名字中间搜索
      const matchMiddle = nameLower.includes(searchValue);
      // 2.3 支持拼音全拼匹配
      const matchPinyin = PinyinMatch.match(name, searchValue);
      // 2.4 支持跳字匹配
      const matchSkip = checkSkipMatch(name, searchValue);
      
      return matchStart || matchMiddle || matchPinyin !== false || matchSkip;
    });

    // 3. 搜索结果按照匹配规则排序：
    // - 开头匹配的排在最前面
    // - 拼音开头匹配的排在第二位
    // - 连续匹配的排在第三位
    // - 跳字匹配的排在最后
    return filteredList.sort((a, b) => {
      const aName = a.speakerName[0];
      const bName = b.speakerName[0];
      const aNameLower = aName.toLowerCase();
      const bNameLower = bName.toLowerCase();

      // 计算匹配优先级（数字越小优先级越高）
      const getPriority = (name: string, nameLower: string) => {
        if (nameLower.startsWith(searchValue)) return 0; // 字符开头匹配
        const pinyinMatch = PinyinMatch.match(name, searchValue);
        if (pinyinMatch && pinyinMatch[0] === 0) return 1; // 拼音开头匹配
        if (nameLower.includes(searchValue)) return 2; // 连续匹配
        if (checkSkipMatch(name, searchValue)) return 3; // 跳字匹配
        return 4; // 其他匹配
      };

      const aPriority = getPriority(aName, aNameLower);
      const bPriority = getPriority(bName, bNameLower);

      return aPriority - bPriority;
    });
  }, [speakerList, inputValue, originValue]);

  const handleEditSingle = () => {
    if (editDisabled) return;
    onEditSingle(inputValue);
  };

  const handleEditAll = () => {
    if (editDisabled) return;
    onEditAll?.(inputValue, originValue);
  };

  const handleSelectRole = (val: string) => {
    setInputValue(val.trim());
  };

  const options = useMemo(() => {
    return [
      {
        id: "DELETE",
        label: "删除",
      },
    ];
  }, []);

  const handlSelectOptions = (item: { id: string; label: ReactNode }, name: string) => {
    if (item.id === "DELETE") {
      fridayReporter.pushData({
        eventCode: 'CollaborationPage_Speaker_Delete'
      })
      // 使用 collaborationStore 的删除方法
      collaborationStore.getState().deleteRecentSpeakers([name]);
    }
  };

  const handleInputChange = (value: string) => {
    setInputValue(value);
  }

  return (
    <div className="w-60 p-2 pb-0">
      <InputWithClear
        autoFocus
        className="
          w-full h-8 block p-2 outline-none
          b-1 b-solid b-#DBDEE4 rounded-2.5
          text-3.5 text-mx-secondary font-400
        placeholder:text-mx-quaternary
        focus:b-mx-primary mb-2
        "
        wapperClassName="w-full"
        allowClear={false}
        limitPattern={speakerNameLimitPattern}
        placeholder="输入发言人名字"
        onMyChange={handleInputChange}
        maxLength={10}
        value={inputValue}
      />
      {list.length > 0 && (
        <div className="h-42 overflow-y-hidden pb-2 flex flex-col">
          <div className="overflow-auto flex-1">
            {list.map((item) => {
              const name = item.speakerName[0];
              return (
                <div
                  key={name}
                  onClick={() => {
                    handleSelectRole(name);
                  }}
                  className={`w-full flex flex-row items-center justify-between cursor-pointer hover:bg-#F4F6F9 p-2 rounded-2.5 ${
                    (name === (inputValue || originValue)) && "bg-[rgba(25,29,38,0.05)]"
                  }`}
                >
                  <div className="flex items-center">
                    <SpeakerAvatar
                      speakerName={name}
                      avatarIndex={getAvatarIndex(item.role)}
                      useDefaultAvatarIcon={true}
                      className="size-6 mr-2"
                      fontSize="0.5rem"
                    />
                    <div className="flex-1 text-3.5 text-mx-secondary max-w-32 mx-text-ellipsis" title={name}>
                      {name}
                    </div>
                  </div>
                  <Popover
                    placement="topRight"
                    align={{ offset: [10, 0] }}
                    content={
                      <SelectionList
                        options={options}
                        onSelect={(option) => {
                          handlSelectOptions(option, name);
                        }}
                        stopPropagation
                      />
                    }
                  >
                    <div
                      className="more-icon size-6 ml-1 flex items-center justify-center hover:bg-#ECEEF3 rounded-2"
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <MoreIcon className="size-4" />
                    </div>
                  </Popover>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="h-12 w-full sticky bottom-0">
        <div className="b-t-1 b-t-solid b-t-#eceef3 h-full w-full flex flex-row justify-end items-center gap-2">
          <Button disabled={editDisabled || !inputValue.trim().length} className="h-8 text-3 text-mx-tertiary flex items-center rounded-2.5" onClick={handleEditAll}>
            全局修改
          </Button>
          <Button type="primary" disabled={editDisabled || !inputValue.trim().length} className="h-8 text-3 flex items-center rounded-2.5" onClick={handleEditSingle}>
            单处修改
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CollaborationSpeakerPopover; 